import { Box, Stack, Typography } from "@mui/material";
import { CustomButton } from "../CustomButton";
import React, { ChangeEvent, useRef, useState } from "react";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { ChangeIcon, DeleteIcon, FileIcon } from "../../images";

export const FileItem = ({
  file,
  isDisabled,
  removeFileHandler,
  changeFileHandler,
  isError,
  acceptedExtensions,
  mimeTypes,
}: {
  file: File;
  isDisabled: boolean;
  removeFileHandler: (name: string) => void;
  changeFileHandler?: (fileName: string, file: File) => void;
  isError?: boolean;
  acceptedExtensions: string[];
  mimeTypes?: string[];
}) => {
  const [pdfFile, setPdfFile] = useState<File | null>(file);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const acceptedTypes = mimeTypes ?? acceptedExtensions.map((ext) => `.${ext}`);

  const removeHandler = () => {
    pdfFile && removeFileHandler(pdfFile.name);
  };

  const uploadFileHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const extension = file.name.split(".").pop()?.toLowerCase();
      const isValidExtension =
        extension && acceptedExtensions.includes(extension);
      const isValidMime = !mimeTypes || mimeTypes.includes(file.type);
      if (isValidExtension && isValidMime) {
        changeFileHandler && changeFileHandler(pdfFile?.name!, file);
        setPdfFile(file);
      } else {
        toast.error(
          <CustomToast
            type={"error"}
            text={`You have selected an unsupported file extension. Please select
             a ${acceptedTypes} file and try again.`}
            title={"File upload failed"}
          />,
        );

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  return (
    <Stack gap="8px" alignItems="center" justifyContent="center">
      <Box display="flex" alignItems="center" gap={1} width="100%">
        <Box display="flex" alignItems="center" flexGrow={1} minWidth={0}>
          <FileIcon
            width="24px"
            height="24px"
            color={isError ? "#D32F2F" : "#1D3E70"}
          />
          <Typography
            color={isError ? "#D32F2F" : "#1D3E70"}
            pl={1}
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {pdfFile?.name}
          </Typography>
        </Box>
        {changeFileHandler && (
          <CustomButton
            disabled={isDisabled}
            variant="outlined"
            customColor=""
            customSize="s"
            onClick={() => fileInputRef.current?.click()}
            sx={{
              minWidth: "auto",
              p: 1,
              width: "40px",
              height: "40px",
              border: "1px solid #1D3E70",
            }}
          >
            <ChangeIcon opacity={isDisabled ? "0.4" : "1"} />
          </CustomButton>
        )}

        <CustomButton
          disabled={isDisabled}
          variant="outlined"
          customColor=""
          customSize="s"
          onClick={removeHandler}
          sx={{
            minWidth: "auto",
            p: 1,
            width: "40px",
            height: "40px",
            border: "1px solid #1D3E70",
          }}
        >
          <DeleteIcon opacity={isDisabled ? "0.4" : "1"} />
        </CustomButton>
      </Box>
      <input
        type="file"
        hidden
        ref={fileInputRef}
        accept={acceptedTypes.join(",")}
        onChange={uploadFileHandler}
      />
    </Stack>
  );
};
